import { chunk, zipWith } from 'lodash-es';
import qs from 'qs';

const MARKET_INDEXES = {
	'Singapore Exchange': '^STI',
	'Hong Kong Stock Exchange': '^HSI',
	NASDAQ: '^IXIC',
	'New York Stock Exchange': '^GSPC',
	'American Stock Exchange': '^DJI',
	'London Stock Exchange': '^FTSE'
};

export async function fetchQuotes(tickers: string[], fetch = window.fetch): Promise<QuotesData> {
	const promises = chunk(tickers, 20).map((symbols) =>
		fetch(`/api/quotes?${qs.stringify({ symbols })}`).then(
			(res) => res.json() as unknown as QuotesData
		)
	);
	const chunks = await Promise.all(promises);
	return chunks.reduce((acc, chunk) => ({ ...acc, ...chunk }), {} as QuotesData);
}

export async function fetchSymbols<
	T extends Populated<BaseSymbol, never> = Populated<BaseSymbol, never>
>(symbols: T[], fetch = window.fetch): Promise<SymbolQuote<T>[]> {
	const tickers = symbols.map(({ exchangeTicker }) => exchangeTicker);
	const quotes = await fetchQuotes(tickers, fetch);

	return symbols.map((symbol) => ({
		...quotes[symbol.exchangeTicker],
		symbol
	}));
}

export async function fetchStocks(
	symbols: Populated<BaseSymbol, 'sgxSecurity'>[],
	fetch = window.fetch
): Promise<StockQuote[]> {
	const tickers = symbols.map(({ exchangeTicker }) => exchangeTicker);
	const promises = chunk(tickers, 20).map((symbols) =>
		fetch(`/api/stocks?${qs.stringify({ symbols })}`).then(
			(res) => res.json() as unknown as StockData
		)
	);
	const chunks = await Promise.all(promises);
	return zipWith(chunks.flat(), symbols, (q, s) => ({
		...q,
		name: s.name,
		consensusTargetPrice: s.sgxSecurity?.targetPrice,
		potentialUpsideDownside: s.sgxSecurity?.targetPrice
			? (s.sgxSecurity.targetPrice - q.regularMarketPrice) / q.regularMarketPrice
			: undefined
	}));
}

export async function getValuationData(stockTicker: string): Promise<ValuationData> {
	return fetch(`/api/valuation/${stockTicker}`).then((res) => {
		if (res.ok) {
			return res.json() as unknown as ValuationData;
		}
		throw new Error('Failed to fetch data');
	});
}

export async function getFinancialData(stockTicker: string): Promise<FinancialData> {
	return fetch(`/api/financial-stats/${stockTicker}`).then((res) => {
		if (res.ok) {
			return res.json() as unknown as FinancialData;
		}
		throw new Error('Failed to fetch data');
	});
}

export async function getDividendData(stockTicker: string): Promise<DividendData> {
	return fetch(`/api/dividend-stats/${stockTicker}`).then((res) => {
		if (res.ok) {
			return res.json() as unknown as DividendData;
		}
		throw new Error('Failed to fetch data');
	});
}

export async function getEarningsData(stockTicker: string): Promise<EarningsData> {
	return fetch(`/api/earnings/${stockTicker}`).then((res) => {
		if (res.ok) {
			return res.json() as unknown as EarningsData;
		}
		throw new Error('Failed to fetch data');
	});
}

export async function getPerformancesData(
	symbol: Populated<BaseSymbol, never>
): Promise<PerformancesData> {
	const symbols = [symbol.exchangeTicker, MARKET_INDEXES[symbol.tradingExchange]];
	return fetch(
		`/api/performances?${qs.stringify(
			{ symbols },
			{
				encodeValuesOnly: true,
				arrayFormat: 'repeat'
			}
		)}`
	).then((res) => res.json() as unknown as PerformancesData);
}

export async function getMarketUpdates(
	params: {
		symbol?: string;
		dividendsLimit?: number;
		newsLimit?: number;
		earningsLimit?: number;
	} = {}
): Promise<RecentHappening[]> {
	const { symbol, dividendsLimit, newsLimit, earningsLimit } = params;
	const response = await fetch(
		`/api/market-updates?${qs.stringify(
			{
				symbol,
				dividendsLimit,
				newsLimit,
				earningsLimit
			},
			{
				encodeValuesOnly: true
			}
		)}`
	);
	return response.json() as unknown as RecentHappening[];
}
